import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { AuthActions } from './auth.actions';
import { switchMap, map, catchError, tap } from 'rxjs/operators';
import { AuthApiService } from '@core/services/api/auth.api.service';
import { concat, defer, EMPTY, from, of } from 'rxjs';
import { Router } from '@angular/router';
import { AUTH_RC } from '@core/constants/routes.constants';
import { AuthResponse } from '@core/models/entities/auth.models';
import { ProfileActions } from '@features/profile/store';
import { Store } from '@ngrx/store';
import { selectUser } from '@features/profile/store/profile.selectors';
import { selectPendingPartnerSubscriptionOfferAfterSignUp } from '@features/auth/store/auth.selectors';
import { DialogApi } from '@shared/modules/dialog/services/dialog.api';
import { PartnerSubscriptionOfferDialogComponent } from '@shared/modules/class-details/components/partner-subscription-offer-dialog/partner-subscription-offer-dialog.component';
import {
  PARTNER_SUBSCRIPTION_OFFER_DIALOG_CONFIG,
  PARTNER_SUBSCRIPTION_OFFER_DIALOG_PANEL_CONFIG,
} from '@shared/modules/class-details/constants/partner-subscription-offer-dialog.constants';
import { User } from '@core/models/entities/user.models';
import { isPartnerSubscriptionOfferSource } from '@core/constants/partner-subscription-offer.constants';
import { BranchService } from '@core/services/helpers/branch.service';

function shouldShowPartnerSubscriptionOfferDialog(user: AuthResponse['user']): boolean {
  return Boolean(
    user && typeof user === 'object' && 'source' in user && isPartnerSubscriptionOfferSource((user as User).source)
  );
}

@Injectable()
export class AuthEffects {
  constructor(
    private actions$: Actions,
    private router: Router,
    private authApiService: AuthApiService,
    private store: Store,
    private dialogApi: DialogApi,
    private branchService: BranchService
  ) {}

  checkEmail$ = createEffect(() => {
    return defer(() =>
      this.actions$.pipe(
        ofType(AuthActions.checkEmail),
        switchMap(({ email }) =>
          this.authApiService.checkEmail({ email }).pipe(
            map(response =>
              AuthActions.saveEmailResult({
                existing_user: response.existing_user,
                company: response.company,
                email,
              })
            ),
            catchError(error => of(AuthActions.error({ error: error.message })))
          )
        )
      )
    );
  });

  signIn$ = createEffect(() => {
    return defer(() =>
      this.actions$.pipe(
        ofType(AuthActions.signIn),
        switchMap(payload =>
          this.authApiService.logIn(payload).pipe(
            switchMap((res: AuthResponse) =>
              concat(of(ProfileActions.saveUser({ user: res.user })), of(AuthActions.navigateToOnDemand()))
            ),
            catchError(error => of(AuthActions.signInFailure({ error: error.message })))
          )
        )
      )
    );
  });

  signUp$ = createEffect(() => {
    return defer(() =>
      this.actions$.pipe(
        ofType(AuthActions.signUp),
        switchMap(payload =>
          this.authApiService.signUp(payload).pipe(
            tap(() => {
              this.branchService.clearPartnerReferralSource();
              this.branchService.clearData();
            }),
            switchMap(res =>
              concat(
                of(ProfileActions.saveUser({ user: res.user })),
                of(
                  AuthActions.setPendingPartnerSubscriptionOfferAfterSignUp({
                    pending: shouldShowPartnerSubscriptionOfferDialog(res.user),
                  })
                ),
                of(AuthActions.navigateToOnDemand())
              )
            ),
            catchError(error => of(AuthActions.error({ error: error.message })))
          )
        ),
        catchError(error => of(AuthActions.error({ error: error.message })))
      )
    );
  });

  forgotPassword$ = createEffect(() => {
    return defer(() =>
      this.actions$.pipe(
        ofType(AuthActions.forgotPassword),
        switchMap(payload =>
          this.authApiService.forgotPassword(payload).pipe(
            map(res => AuthActions.forgotPasswordSuccess({ success: res })),
            catchError(error => of(AuthActions.error({ error: error.message })))
          )
        ),
        catchError(error => of(AuthActions.error({ error: error.message })))
      )
    );
  });

  resetPassword$ = createEffect(() => {
    return defer(() =>
      this.actions$.pipe(
        ofType(AuthActions.resetPassword),
        switchMap(payload =>
          this.authApiService.resetPassword(payload).pipe(
            map(res => AuthActions.resetPasswordSuccess({ success: res })),
            catchError(error => of(AuthActions.error({ error: error.message })))
          )
        ),
        catchError(error => of(AuthActions.error({ error: error.message })))
      )
    );
  });

  resetPasswordSuccess$ = createEffect(
    () => {
      return defer(() =>
        this.actions$.pipe(
          ofType(AuthActions.resetPasswordSuccess),
          tap(() => {
            this.router.navigate([`/${AUTH_RC.root}/${AUTH_RC.children.logIn}`]);
          })
        )
      );
    },
    { dispatch: false }
  );

  patchPasswordUser$ = createEffect(() => {
    return defer(() =>
      this.actions$.pipe(
        ofType(AuthActions.patchPasswordUser),
        switchMap(payload =>
          this.authApiService.patchPasswordUser(payload).pipe(
            map(() => AuthActions.navigateToProfile()),
            catchError(error => of(AuthActions.error({ error: error.message })))
          )
        ),
        catchError(error => of(AuthActions.error({ error: error.message })))
      )
    );
  });

  navigateToProfile$ = createEffect(
    () => {
      return defer(() =>
        this.actions$.pipe(
          ofType(AuthActions.navigateToProfile),
          tap(() => {
            this.router.navigate(['/profile/view']);
          })
        )
      );
    },
    { dispatch: false }
  );

  navigateToOnDemand$ = createEffect(() => {
    return defer(() =>
      this.actions$.pipe(
        ofType(AuthActions.navigateToOnDemand),
        concatLatestFrom(() => [
          this.store.select(selectPendingPartnerSubscriptionOfferAfterSignUp),
          this.store.select(selectUser),
        ]),
        switchMap(([, pendingOffer, { user }]) => {
          const path = this.authApiService.isInstructor() ? '/my-activity' : '/live';
          return from(this.router.navigate([path])).pipe(
            switchMap(() => {
              if (!pendingOffer) {
                return EMPTY;
              }
              if (!user || !isPartnerSubscriptionOfferSource(user.source)) {
                return of(AuthActions.setPendingPartnerSubscriptionOfferAfterSignUp({ pending: false }));
              }
              this.dialogApi.openCustomDialog(
                PartnerSubscriptionOfferDialogComponent,
                { ...PARTNER_SUBSCRIPTION_OFFER_DIALOG_CONFIG, data: { user: user as User } },
                { ...PARTNER_SUBSCRIPTION_OFFER_DIALOG_PANEL_CONFIG }
              );
              return of(AuthActions.setPendingPartnerSubscriptionOfferAfterSignUp({ pending: false }));
            })
          );
        })
      )
    );
  });
}
